import {
    validateEmail,
    validateMembershipNumber,
} from '../abstract/validation';
import { getTimeZone, getCurrentDate } from '../abstract/time';
import { translate } from '../globals/translations';

/**
 * processForSubmission
 *
 * Process form data: validate and clean up
 *
 * @since 0.5
 *
 * @param {object} form The DOM form object to process
 * @param {integer} step The step we are processing
 * @param {object} data The data we need to send to API
 * @param {arary} images The images to be sent to the API.
 *
 * @return {object} status if form processing is successful or not
 *
 */
export function runThroughValidation(form, step, data, images) {
    const response = {
        completed: true,
        status: 200,
        messages: [],
    };

    const spamStatus = checkForSpam(form);

    if (spamStatus) {
        response.status.completed = false;
        window.location.href = '/';

        return null;
    }

    if (step == 1) {
        const validationStatus = validateForm(form);

        if (!validationStatus) {
            response.completed = false;
            response.messages.unshift(
                'Please fill out all the required fields'
            );
            response.status = 444;
            response.form = form;

            return response;
        }
    }

    if (step == 3) {
        const validationStatus = validateForm(form);

        if (!validationStatus) {
            response.completed = false;
            response.messages.unshift(
                'Please fill out all the required fields'
            );
            response.status = 444;
            response.form = form;

            return response;
        }
    }

    if (step == 1 && images.length > 5) {
        response.completed = false;
        response.messages.unshift('Maximum of 5 images allowed.');
        response.status = 445;
        response.form = form;

        return response;
    }

    response.messages.unshift('Form is ready to submit');
    response.data = data;

    return response;
}

export function scrollToTargetAdjusted(element) {
    const headerOffset = 100;
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

    window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
    });
}

/**
 * checkForSpam
 *
 * Check if our honey pot field is filled up. If yes,
 * it means that it was filled up by a bot
 *
 * @since 0.5
 *
 * @param {object} form The DOM form object to validate
 *
 * @return {boolean} status if spam or not
 *
 */
export function checkForSpam(form) {
    const formFields = form.elements;
    const honeyPotField = formFields[0];
    const status = honeyPotField.value !== '' ? true : false;

    return status;
}

/**
 * validateForm
 *
 * Validate form and make sure all fields are filled out. This method
 * assumes that all fields are required.
 *
 * @since 0.5
 *
 * @param {object} form The DOM form object to validate
 *
 * @return {boolean} status if validation is successful or not
 *
 */
export function validateForm(form) {
    let status = true;
    const formFields = form.elements;

    for (let i = 0; i < formFields.length; i++) {
        if (i == 0 || formFields[i].type == 'submit') {
            continue;
        }

        const formField = formFields[i];

        if (formField.classList.contains('required')) {
            if (formField.value == '') {
                status = false;

                break;
            }
        }

        if (formField.classList.contains('g-recaptcha-response')) {
            if (formField.value == '') {
                status = false;

                break;
            }
        }

        if (status && formField.name == 'email') {
            status = validateEmail(formField.value.trim());
        }

        if (status && formField.name == 'membership') {
            status = validateMembershipNumber(formField.value.trim());
        }
    }

    return status;
}

/**
 * validateRoadUsers
 *
 * Validate form and make sure all fields are filled out. This method
 * assumes that all fields are required.
 *
 * @since 0.5
 *
 * @param {object} form The DOM form object to validate
 *
 * @return {boolean} status if validation is successful or not
 *
 */
export function validateRoadUsers(form) {
    let status = false;
    const formFields = form.elements;
    let checkboxChecked = false;

    for (let i = 0; i < formFields.length; i++) {
        if (i == 0 || formFields[i].type == 'submit') {
            continue;
        }

        const formField = formFields[i];

        if (formField.type != 'checkbox') {
            continue;
        }

        if (formField.checked) {
            checkboxChecked = true;
            break;
        }
    }

    status = checkboxChecked ? true : false;

    return status;
}

/**
 * highlightRequiredFields
 *
 * Trigger the error state of the required input fields and messages
 *
 * @since 0.5
 *
 * @param {object} form The DOM form object to trigger
 *
 * @return {n/a}
 *
 */
export function highlightRequiredField(form) {
    const formFields = form.elements;

    for (let i = 0; i < formFields.length; i++) {
        if (i == 0 || formFields[i].type == 'submit') {
            continue;
        }

        const formField = formFields[i];
        const formFieldName = formField.name;
        const errorMsg = form.querySelector(
            '[data-for="' + formFieldName + '"]'
        );

        if (formField.classList.contains('required')) {
            if (
                formField.value == '' ||
                (formField.type == 'checkbox' && !formField.checked) ||
                (formField.name == 'email' && !validateEmail(formField.value))
            ) {
                if (errorMsg.classList.contains('-hidden')) {
                    errorMsg.classList.remove('-hidden');
                }

                if (!formField.classList.contains('error')) {
                    formField.classList.add('error');
                }
            }
        }

        if (formField.classList.contains('validate-membership')) {
            if (!validateMembershipNumber(formField.value)) {
                if (errorMsg.classList.contains('-hidden')) {
                    errorMsg.classList.remove('-hidden');
                }

                if (!formField.classList.contains('error')) {
                    formField.classList.add('error');
                }
            }
        }
    }
}

export function bundleDataForStepOne(step_one_state, step_one_props) {
    let formattedData = {
        road: {
            address: step_one_state.roadObject.address
                ? step_one_state.roadObject.address
                : '',
            data: step_one_state.roadObject.data
                ? step_one_state.roadObject.data
                : {},
            location: {
                lat: step_one_state.roadObject.location
                    ? step_one_state.roadObject.location.lat
                    : '',
                lng: step_one_state.roadObject.location
                    ? step_one_state.roadObject.location.lng
                    : '',
            },
        },
        crossroad: {
            address: step_one_state.crossroadObject.address
                ? step_one_state.crossroadObject.address
                : '',
            location: {
                lat: step_one_state.crossroadObject.location
                    ? step_one_state.crossroadObject.location.lat
                    : '',
                lng: step_one_state.crossroadObject.location
                    ? step_one_state.crossroadObject.location.lng
                    : '',
            },
        },
        marker: step_one_state.markerLocation
            ? step_one_state.markerLocation
            : '',
        method: step_one_state.manualVote ? 'manual' : 'lookup',
        step: '1',
        ip: step_one_props.userIp,
        club: null,
        sid: "It's not who I am underneath, but what I do that defines me.", // @TODO: Needs to be dynamic, pull from location detection session
    };

    const acInputField = document.getElementById('additional_comments');
    let additionalComments =
        acInputField.value !== '' ? acInputField.value.trim() : '';
    formattedData.additional_comments = additionalComments;

    const cityInputField = document.getElementById('city_nofill');
    let city =
        step_one_state.city !== '' && step_one_state.city !== undefined
            ? step_one_state.city
            : cityInputField.value;

    formattedData.city = city;

    const provinceInputField = document.getElementById('province_nofill');
    let province =
        step_one_state.province !== '' && step_one_state.province !== undefined
            ? step_one_state.province
            : provinceInputField.value;
    formattedData.province = province;

    const emailInputField = document.getElementById('email');
    let email =
        emailInputField.value !== '' ? emailInputField.value.trim() : '';
    formattedData.email = email;

    formattedData.road = JSON.stringify(formattedData.road);
    formattedData.crossroad = JSON.stringify(formattedData.crossroad);

    if (formattedData.marker !== '') {
        formattedData.marker = JSON.stringify(formattedData.marker);
    }

    const privacyPolicyField = document.getElementById('privacy_policy');
    let privacy_policy =
        privacyPolicyField.value !== '' &&
        privacyPolicyField.value !== undefined
            ? privacyPolicyField.value
            : '';
    formattedData.privacy_policy = privacy_policy;

    const audienceTargetingField =
        document.getElementById('audience_targeting');
    formattedData.audience_targeting =
        audienceTargetingField &&
        audienceTargetingField.value !== '' &&
        audienceTargetingField.value !== undefined
            ? audienceTargetingField.value
            : '';

    formattedData.completed = true;

    const date = getCurrentDate();

    formattedData.is_in_restricted_time = false;

    formattedData.vote_timezone = getTimeZone(date.instance);

    formattedData.vote_time = date.dateTime;

    return formattedData;
}

export function bundleData(step_one_state, step_one_props) {
    let formattedData = {
        road: {
            address: step_one_state.roadObject.address
                ? step_one_state.roadObject.address
                : '',
            data: step_one_state.roadObject.data
                ? step_one_state.roadObject.data
                : {},
            location: {
                lat: step_one_state.roadObject.location
                    ? step_one_state.roadObject.location.lat
                    : '',
                lng: step_one_state.roadObject.location
                    ? step_one_state.roadObject.location.lng
                    : '',
            },
        },
        crossroad: {
            address: step_one_state.crossroadObject.address
                ? step_one_state.crossroadObject.address
                : '',
            location: {
                lat: step_one_state.crossroadObject.location
                    ? step_one_state.crossroadObject.location.lat
                    : '',
                lng: step_one_state.crossroadObject.location
                    ? step_one_state.crossroadObject.location.lng
                    : '',
            },
        },
        marker: step_one_state.markerLocation
            ? step_one_state.markerLocation
            : '',
        method: step_one_state.manualVote ? 'manual' : 'lookup',
        step: '1',
        ip: step_one_props.userIp,
        club: null,
        sid: "It's not who I am underneath, but what I do that defines me.", // @TODO: Needs to be dynamic, pull from location detection session
        road_problems: [],
    };

    const acInputField = document.getElementById('additional_comments');
    let additionalComments =
        acInputField.value !== '' ? acInputField.value.trim() : '';
    formattedData.additional_comments = additionalComments;

    const cityInputField = document.getElementById('city_nofill');
    let city =
        step_one_state.city !== '' && step_one_state.city !== undefined
            ? step_one_state.city
            : (cityInputField && cityInputField.value) ?? '';

    formattedData.city = city;

    const provinceInputField = document.getElementById('province_nofill');
    let province =
        step_one_state.province !== '' && step_one_state.province !== undefined
            ? step_one_state.province
            : (provinceInputField && provinceInputField.value) ?? '';
    formattedData.province = province;

    const emailInputField = document.getElementById('email');
    let email =
        emailInputField.value !== '' ? emailInputField.value.trim() : '';
    formattedData.email = email;

    formattedData.road = JSON.stringify(formattedData.road);
    formattedData.crossroad = JSON.stringify(formattedData.crossroad);

    if (formattedData.marker !== '') {
        formattedData.marker = JSON.stringify(formattedData.marker);
    }

    const privacyPolicyField = document.getElementById('privacy_policy');
    let privacy_policy =
        privacyPolicyField.value !== '' &&
        privacyPolicyField.value !== undefined
            ? privacyPolicyField.value
            : '';
    formattedData.privacy_policy = privacy_policy;

    const audienceTargetingField =
        document.getElementById('audience_targeting');
    formattedData.audience_targeting =
        audienceTargetingField &&
        audienceTargetingField.value !== '' &&
        audienceTargetingField.value !== undefined
            ? audienceTargetingField.value
            : '';

    formattedData.completed = true;

    const date = getCurrentDate();

    formattedData.is_in_restricted_time = false;

    formattedData.vote_timezone = getTimeZone(date.instance);

    formattedData.vote_time = date.dateTime;

    if (step_one_state.roadProblems !== undefined) {
        formattedData.road_problems = step_one_state.roadProblems;
        if (step_one_state.roadProblems.includes(translate('other'))) {
            let temp = formattedData.road_problems;
            let index = temp.indexOf(translate('other'));

            if (index !== -1) {
                temp[index] = step_one_state.otherReasons;
            }

            formattedData.road_problems = temp;
        }
    }

    return formattedData;
}
